import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Box,
  Text,
  Checkbox,
  Grid,
  GridItem,
} from "@chakra-ui/react";

import { LinearRangeSlider } from "components/linear-range-slider";
import { Filters, FilterPopupProps } from "./custom-types";
import { textStyle } from "theme/names";
import { InputWithList } from "../input-with-list";
 
const MAX_FOLLOWERS = 105000;

export function FiltersPopup({
  isOpen,
  onClose,
  filters,
  onApplyFilters,
  mode,
}: FilterPopupProps) {
  const [datePublished, setDatePublished] = useState<[number, number]>([0, 7]);
  const [followers, setFollowers] = useState<[number, number]>([
    0, 999999999,
  ]);
  const [includeList, setIncludeList] = useState<string[]>([]);
  const [excludeList, setExcludeList] = useState<string[]>([]);
  const [noMediaAndLinks, setNoMediaAndLinks] = useState<boolean>(false);
  const [noHashtags, setNoHashtags] = useState<boolean>(false);
  

  const formatFilters = (): Filters => {
    let filterData: Filters = {
      minDaysOld: datePublished[0],
      maxDaysOld: datePublished[1],
      minFollowers: followers[0],
      maxFollowers: followers[1] >= MAX_FOLLOWERS ? 999999999 : followers[1],
      negativeKeywords: excludeList,
      positiveKeywords: includeList,
      noMediaAndLinks,
      noHashtags,
    };

    return filterData;
  };

  useEffect(() => {
    const {
      minDaysOld,
      maxDaysOld,
      minFollowers,
      maxFollowers,
      positiveKeywords,
      negativeKeywords,
      noMediaAndLinks,
      noHashtags,
    } = filters;
    
    setFollowers([minFollowers || 0, maxFollowers || 999999999]);
    setDatePublished([minDaysOld || 0, maxDaysOld || 7]);
    setIncludeList(positiveKeywords);
    setExcludeList(negativeKeywords);
    setNoMediaAndLinks(noMediaAndLinks || false);
    setNoHashtags(noHashtags || false);
  }, [filters]);

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Filters</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Grid
            templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
            gap={{ base: "8", md: "12" }}
            mx="0"
          >
            <GridItem>
              <FormControl>
                <FormLabel>Date Published</FormLabel>
                <Text textStyle={textStyle["body.medium.light"]}>
                  We can only find the last 10 posts from a user
                </Text>
                <LinearRangeSlider
                  rangeTextPrefix="days ago"
                  rangeTextSuffix="Max"
                  min={0}
                  max={30}
                  ultimateMaxValue={31}
                  step={1}
                  unlimitedMaxValue={false}
                  value={datePublished}
                  onChange={(val) => setDatePublished(val)}
                  //disableLeftThumb={true}
                />
              </FormControl>
            </GridItem>
           
            <GridItem>
              <FormControl>
                <FormLabel>Followers</FormLabel>
                <Text textStyle={textStyle["body.medium.light"]}>
                  Posts from people with atleast / at most X followers.
                </Text>
                <LinearRangeSlider
                  rangeTextPrefix="followers"
                  min={0}
                  max={MAX_FOLLOWERS}
                  ultimateMaxValue={999999999}
                  step={5000}
                  unlimitedMaxValue={true}
                  value={followers}
                  onChange={(val) => setFollowers(val)}
                />
              </FormControl>
            </GridItem>
          </Grid>

          <Box mt="10">
            <Text fontWeight="medium">Keywords</Text>
            <Text textStyle={textStyle["body.medium.light"]} mt={1}>
                Include or exclude keywords from search results.
              </Text>
            <Grid
              templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
              gap={{ base: "8", md: "8" }}
              mx="0"
              mt="6"
              alignItems={"center"}
            >
              <GridItem display="flex" gap={3} alignItems={"center"}>
                <Text fontSize="sm" mt="1" textStyle={textStyle["body.medium.light"]}
                    fontWeight="600">
                  Include
                </Text>
                <InputWithList
                  onChange={(list) => setIncludeList(list)}
                  initValue={includeList}
                  inputProps={{
                    width: "120px"
                  }}
                />
              </GridItem>
              <GridItem display="flex" gap={3} alignItems={"center"}>
                <Text fontSize="sm" mt="1" textStyle={textStyle["body.medium.light"]}
                    fontWeight="600">
                  Exclude
                </Text>
                <InputWithList
                  onChange={(list) => setExcludeList(list)}
                  initValue={excludeList}
                  inputProps={{
                    width: "120px",
                  }}
                />
              </GridItem>
            </Grid>
          </Box>
          <Box mt="10">
            <Text fontWeight="medium">Other filters</Text>
            <Grid
              templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
              gap="8"
              mx="0"
              mt="6"
              alignItems={"center"}
            >
              <GridItem>
                  <Checkbox
                    isChecked={noHashtags}
                    onChange={(e) => setNoHashtags(e.target.checked)}
                  >
                    Remove posts with hashtags
                  </Checkbox>
                </GridItem>
              <GridItem>
                <Checkbox
                  isChecked={noMediaAndLinks}
                  onChange={(e) => setNoMediaAndLinks(e.target.checked)}
                >
                  Remove posts with links & media<br/>
                  (images, videos, documents, etc.)
                </Checkbox>
              </GridItem>
            </Grid>
          </Box>
        </ModalBody>

        <ModalFooter justifyContent="center">
          <Button mr={3} variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="twitter"
            onClick={() => {
              onClose();
              onApplyFilters(formatFilters());
            }}
            variant={"primary"}
          >
            Apply Filters
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
